<template>
  <div ref="root" class="calling-tracking">
    <div v-if="loading" class="d-flex justify-content-end px-2 pt-50 pb-25">
      <b-spinner small variant="secondary" />
    </div>

    <div v-if="error && !loading && !items.length" class="text-center py-2 text-muted small px-1">
      {{ error }}
    </div>
    <div
      v-else-if="!loading && !items.length && !hasCallbackRows"
      class="text-center py-2 text-muted small"
    >
      No output to review
    </div>
    <b-list-group v-else-if="items.length" flush>
      <b-list-group-item
        v-for="(c, idx) in visibleItems"
        :key="c.sid || c.rowKey || ('ct-' + idx)"
        class="px-2 py-50 calling-tracking__row"
      >
        <div class="alert-call-row">
          <div class="alert-call-row__info">
            <div class="small font-weight-bold text-truncate">{{ c.candidateLabel || c.phone || '—' }}</div>
            <small class="text-muted d-block text-truncate">
              {{ c.date || '—' }}<template v-if="c.contactNo"> · {{ c.contactNo }}</template>
            </small>
            <small
              v-if="c.attentionKind === 'flagged' && c.aiVerdict"
              class="text-danger d-block text-truncate"
            >
              {{ c.aiVerdict }}
            </small>
            <small
              v-if="c.attentionKind === 'flagged' && c.candidateStatus"
              class="text-muted d-block text-truncate"
            >
              {{ c.candidateStatus }}
            </small>
            <small
              v-if="c.attentionKind === 'flagged' && c.whyShared"
              class="text-secondary d-block text-truncate"
            >
              Why shared: {{ c.whyShared }}
            </small>
          </div>
          <b-badge
            :variant="badgeVariant(c.attentionKind)"
            class="flex-shrink-0"
            style="font-size:0.65rem;"
          >
            {{ c.attentionBadge || 'Needs attention' }}
          </b-badge>
        </div>
      </b-list-group-item>
    </b-list-group>
  </div>
</template>

<script>
import {
  BBadge,
  BListGroup,
  BListGroupItem,
  BSpinner,
} from 'bootstrap-vue'
import {
  partitionAttentionExotelCalls,
} from '@/utils/ai-calling-tracking'

export default {
  name: 'RecruiterCallingTracking',
  components: {
    BBadge,
    BListGroup,
    BListGroupItem,
    BSpinner,
  },
  props: {
    previewLimit: {
      type: Number,
      default: 20,
    },
    canViewOrg: {
      type: Boolean,
      default: false,
    },
    canViewTeam: {
      type: Boolean,
      default: false,
    },
    refreshKey: {
      type: Number,
      default: 0,
    },
    /** Full-range (or current-page) analytics rows from the parent live period set. */
    sourceCalls: {
      type: Array,
      default() { return [] },
    },
    /** True when sourceCalls is the complete date / emp / mgr set. */
    sourceComplete: {
      type: Boolean,
      default: false,
    },
    /** Parent is paging the unfiltered live period set. */
    periodLoading: {
      type: Boolean,
      default: false,
    },
    startDate: {
      type: String,
      default: '',
    },
    endDate: {
      type: String,
      default: '',
    },
    hasCallbackRows: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      loaded: false,
      error: '',
      items: [],
      notAnsweredItems: [],
      missedItems: [],
      notAddedItems: [],
      flaggedItems: [],
      fetchedRows: [],
      fetchScope: 'my',
    }
  },
  computed: {
    visibleItems() {
      return this.items.slice(0, this.previewLimit)
    },
  },
  watch: {
    refreshKey() {
      this.scheduleApplyFromSources()
    },
    sourceCalls: {
      immediate: true,
      handler() {
        this.scheduleApplyFromSources()
        if (this.sourceComplete) this.loaded = true
      },
    },
    sourceComplete(complete) {
      if (complete) {
        this.scheduleApplyFromSources()
        this.loaded = true
      }
    },
    periodLoading() {
      this.scheduleApplyFromSources()
    },
    items: {
      immediate: true,
      handler(list) {
        this.$emit('count', Array.isArray(list) ? list.length : 0)
      },
    },
  },
  mounted() {
    this.observeVisibility()
  },
  beforeDestroy() {
    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
    if (this._applyTimer) {
      clearTimeout(this._applyTimer)
      this._applyTimer = null
    }
  },
  methods: {
    observeVisibility() {
      const el = this.$refs.root || this.$el
      if (!el || typeof IntersectionObserver === 'undefined') {
        this.ensureLoaded()
        return
      }
      this._observer = new IntersectionObserver((entries) => {
        if (entries.some(entry => entry.isIntersecting)) {
          this.ensureLoaded()
        }
      }, { threshold: 0.15 })
      this._observer.observe(el)
    },
    scheduleApplyFromSources() {
      if (this._applyTimer) return
      this._applyTimer = setTimeout(() => {
        this._applyTimer = null
        this.applyFromSources()
        if (this.sourceComplete) this.loaded = true
      }, 120)
    },
    ensureLoaded() {
      if (this.loaded || this.loading) return
      this.applyFromSources()
      if (this.sourceComplete) {
        this.loaded = true
        return
      }
      // Parent (My Calls) owns the full-range drain — do not start a second one.
    },
    badgeVariant(kind) {
      if (kind === 'not_added' || kind === 'flagged') return 'light-danger'
      if (kind === 'not_answered' || kind === 'missed') return 'light-warning'
      return 'light-secondary'
    },
    emitAttention() {
      this.$emit('attention', {
        items: this.items,
        notAnswered: this.notAnsweredItems,
        missed: this.missedItems,
        notAdded: this.notAddedItems,
        flagged: this.flaggedItems,
        scope: this.fetchScope,
      })
    },
    applyPartition(rows) {
      const partitioned = partitionAttentionExotelCalls(rows)
      this.items = partitioned.items
      this.notAnsweredItems = partitioned.notAnswered
      this.missedItems = partitioned.missed
      this.notAddedItems = partitioned.notAdded
      this.flaggedItems = partitioned.flagged || []
      this.emitAttention()
    },
    applyFromSources() {
      const source = Array.isArray(this.sourceCalls) ? this.sourceCalls : []
      // Parent owns the filtered period set (including an empty client/status match).
      if (this.sourceComplete || this.periodLoading || this.startDate || source.length) {
        this.applyPartition(source)
        return
      }
      const fetched = Array.isArray(this.fetchedRows) ? this.fetchedRows : []
      this.applyPartition(source.concat(fetched))
    },
    fetchCalls() {
      // Parent live period set is the Output source — never drain call-analytics here.
      this.applyFromSources()
      this.loaded = this.sourceComplete || this.periodLoading || Boolean(this.startDate)
    },
  },
}
</script>

<style scoped>
.alert-call-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
}
.alert-call-row__info {
  flex: 1 1 auto;
  min-width: 0;
}
.alert-call-row__info > * + * {
  margin-top: 0.125rem;
}
</style>
